import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useApplicantStore } from '@/features/applicant/applicantStore';
import { useAuthStore } from './auth-store';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000/api/v1';

export const api = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30_000,
});

// Applicant-flow writes use the applicant token. GETs on the same paths
// belong to the admin console and must use the admin access token.
const APPLICANT_POST_ENDPOINTS = ['/registrations', '/uploads', '/cis/verify'];
const APPLICANT_DELETE_ENDPOINTS = ['/uploads'];

const isApplicantRequest = (config: InternalAxiosRequestConfig): boolean => {
  const url = config.url ?? '';
  const method = (config.method ?? 'get').toLowerCase();
  if (method === 'post') return APPLICANT_POST_ENDPOINTS.some((p) => url.startsWith(p));
  if (method === 'delete') return APPLICANT_DELETE_ENDPOINTS.some((p) => url.startsWith(p));
  return false;
};

api.interceptors.request.use((config) => {
  if (isApplicantRequest(config)) {
    const applicantToken = useApplicantStore.getState().token;
    if (applicantToken) {
      config.headers.set('Authorization', `Bearer ${applicantToken}`);
      return config;
    }
  }
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

let refreshPromise: Promise<string> | null = null;

const doRefresh = async () => {
  const res = await axios.post<{ accessToken: string }>(
    `${baseURL}/auth/refresh`,
    null,
    { withCredentials: true },
  );
  useAuthStore.getState().setSession(res.data.accessToken, useAuthStore.getState().user);
  return res.data.accessToken;
};

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const isAuthCall = original?.url?.includes('/auth/');
    const isApplicantCall =
      original?.url?.includes('/applicants/') || (original && isApplicantRequest(original));
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !isAuthCall &&
      !isApplicantCall
    ) {
      original._retried = true;
      try {
        refreshPromise ??= doRefresh().finally(() => {
          refreshPromise = null;
        });
        const token = await refreshPromise;
        original.headers.set('Authorization', `Bearer ${token}`);
        return api.request(original);
      } catch {
        useAuthStore.getState().clearSession();
      }
    }
    return Promise.reject(error);
  },
);

export interface ApiError {
  error: { code: string; message: string; details?: unknown };
}

export const extractValidationIssues = (
  err: unknown,
): Array<{ path: string; message: string }> => {
  if (!axios.isAxiosError(err)) return [];
  const details = (err.response?.data as ApiError | undefined)?.error?.details as
    | { issues?: Array<{ path: string; message: string }> }
    | undefined;
  return details?.issues ?? [];
};
